import * as React from 'react';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { useHistory } from 'react-router-dom';

// COMPONENTS
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../components/Button';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/svg/chevron-right.svg';
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';

// TYPES
import { DomainFeedbackCategory } from '../../api/models';
import { FileType } from '../../components/FileUpload';

export interface FeedbackFormTypes {
	subject?: string;
	occurrence?: string;
	category?: DomainFeedbackCategory;
	summary?: string;
	anonymously?: boolean;
	isResponseRequired?: boolean;
	anonymousContact?: string;
	createAttachments?: string[];
	attachments: FileType[];
}

const FeedbackSubmit: React.FC = () => {
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	// Set up Form State and initial Form Data
	const initialFeedbackFormData: FeedbackFormTypes = {
		subject: '',
		occurrence: '',
		category: undefined,
		summary: '',
		anonymously: false,
		anonymousContact: undefined,
		isResponseRequired: true,
		createAttachments: [],
		attachments: []
	};

	const [feedbackFormData, setFeedbackFormData] = React.useState(initialFeedbackFormData);

	// State to control active step
	const [activeStep, setActiveStep] = React.useState('step1');

	// Logic for Back Button
	const mobileStepHandler = () => {
		if (activeStep === 'step2') {
			setActiveStep('step1');
		}

		if (activeStep === 'step1') {
			history.push('/feedback');
		}
	};

	React.useEffect(() => {
		const pageState = history.location.state;

		if (pageState === undefined || pageState.activeStep === undefined || pageState.activeStep === null) {
			history.replace(window.location.pathname, { activeStep });
		} else if (pageState.activeStep !== activeStep) {
			history.push(window.location.pathname, { activeStep });
		}

		let unlisten = history.listen((location, action) => {
			if (
				action === 'POP' &&
				location.state &&
				location.state.activeStep !== undefined &&
				location.state.activeStep !== null
			) {
				setActiveStep(location.state.activeStep);
			}
		});

		return unlisten;
	}, [activeStep]);

	const goBack = () => {
		mobileStepHandler();
	};

	return (
		<div className="page-general-layout">
			<div className="page-heading">
				<Button className="btn-text-icon" onClick={goBack} buttonIcon={<ArrowLeft className="link-icon" />}>
					Back
				</Button>

				<h1 className="h1">Submit feedback</h1>
			</div>

			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="form-steps-controller-desktop small d-none d-lg-flex">
									<div
										className={`steps-name ${
											activeStep === 'step1' || activeStep === 'step2' || activeStep === 'step3' ? 'active' : ''
										}`}
										onClick={() => setActiveStep('step1')}
									>
										1. Feedback
									</div>

									<ArrowRightIcon className="step-icon" />

									<div
										className={`steps-name ${activeStep === 'step2' || activeStep === 'step3' ? 'active' : ''}`}
										onClick={() => setActiveStep('step2')}
									>
										2. Review & Submit
									</div>
								</div>

								<div className="form-steps-controller-mobile d-flex d-lg-none">
									<div className="steps-name-group">
										{activeStep === 'step1' && <div className="steps-name active">1. Feedback details</div>}

										{activeStep === 'step2' && <div className="steps-name active">2. Review & Submit</div>}
									</div>

									<div className="controller-group">
										<div
											className={`controller-wrapper ${activeStep === 'step2' ? 'active' : ''}`}
											onClick={mobileStepHandler}
										>
											<ArrowLeftIcon className="step-controller" />
										</div>

										<div className="controller-wrapper">
											<ArrowRightIcon className="step-controller" />
										</div>
									</div>
								</div>

								<div className="general-divider"></div>

								{activeStep === 'step1' && (
									<StepOne
										feedbackFormData={feedbackFormData}
										setFeedbackFormData={setFeedbackFormData}
										setActiveStep={setActiveStep}
									/>
								)}

								{activeStep === 'step2' && (
									<StepTwo
										feedbackFormData={feedbackFormData}
										setActiveStep={setActiveStep}
										isLoading={isLoading}
										setIsLoading={setIsLoading}
									/>
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default FeedbackSubmit;
